// ═══════════════════════════════════════════════════════════════════════════
//  A20 · C1 — THE ANONYMOUS AUTH-PAGE REPORTER for the licence console.
//
//  ── THE HOLE THIS FILLS ─────────────────────────────────────────────────────
//  <ConsoleReporter> is mounted by AdminHeader, which lives inside PortalShell,
//  which lives inside ProtectedArea. Both of this app's unauthenticated routes
//  sit OUTSIDE that gate (App.tsx: /login, and /reset-password, which is
//  explicitly outside "so it renders whether or not a (recovery) session
//  exists"). So neither had a reporter of any kind. An operator who cannot sign
//  in to the licence console — the console that administers everything else —
//  had no way to tell anyone, which is precisely backwards.
//
//  ── WHAT TRAVELS, AND WHAT DELIBERATELY DOES NOT ────────────────────────────
//   ✔ route          path only. ReportProvider's currentRoute() strips the
//                    query string, and GoTrue's recovery token rides the HASH,
//                    which is not part of `location.pathname` at all.
//   ✔ browser        the whole `client` block: UA, viewport, locale, time zone,
//                    reduced-motion, colour scheme
//   ✔ console        the last N console lines (useConsoleBuffer)
//   ✔ network        method + origin/pathname + status. Never bodies, never
//                    query strings, never the hash.
//   ✘ identity       getIdentity returns {}. There is no session — that is the
//                    whole premise of the surface — so there is nothing
//                    truthful to send. The route stamps nothing either: unlike
//                    /reports/mine it passes no session fields to
//                    bugReportService.ingest, so reporter_user_id stays NULL and
//                    the triage console says "not recorded" rather than
//                    inventing a name. Since the phase-2/3 review the route also
//                    DELETES identity.userId / identity.role from the envelope,
//                    because ingest otherwise falls back to them when the
//                    context has no session — which on this route it never has.
//   ✘ recording      /reports/anonymous strips `captureGzipB64` and forces
//                    capture.captured = false server-side. See useConsoleBuffer.
//
//  ── ⚠ ONE FOLLOW-UP, NAMED RATHER THAN LEFT TO BE DISCOVERED ────────────────
//  The other three surfaces express "console yes, replay no" with ONE flag:
//  `replayDisabled`, which A20 C2 added to the shared reporter core (TPRM7
//  packages/ui) and to customer-portal's port. This app's port does not have it
//  yet — although its strings.ts already carries the `capture.noreplay` key that
//  goes with it — so here the same posture is expressed as `captureDisabled`
//  (which installs no shims at all) PLUS ./useConsoleBuffer (which installs the
//  two that matter). Behaviour is equivalent; the cost is one duplicated file
//  and one imprecise dialog line: it says "session capture is unavailable"
//  rather than "no replay, and here is what IS attached". That under-states what
//  we send, which is the safe direction to be wrong in, but it is not the same
//  sentence the other surfaces show.
//
//  WHEN `replayDisabled` lands in this app's useRecorder/ReportProvider: delete
//  ./useConsoleBuffer.ts, swap `captureDisabled: true` below for
//  `replayDisabled: true`, and drop the capture-substitution block in the
//  transport. Nothing else changes.
//
//  ── SAME SERVER, DIFFERENT DOOR ─────────────────────────────────────────────
//  This posts to the SAME origin every other call in this app already uses
//  (VITE_API_URL), just to /reports/anonymous instead of /reports/mine. That is
//  why it needs no new CORS origin — unlike the TPRM7 apps, which are separate
//  hosts and are listed in infra/ecs/license-backend.a20.json.
//
//  It does NOT go through `apiRequest`: that helper's whole job is to attach the
//  Supabase bearer, and there is none. A plain fetch also keeps the Turnstile
//  token out of any shared header path.
//
//  ── UNCONFIGURED ⇒ NO BUTTON AT ALL. A DECISION, NOT AN OVERSIGHT ───────────
//  Mounted only when VITE_TURNSTILE_SITE_KEY is set. With the server's
//  TURNSTILE_SECRET_KEY unset the route answers 501 to EVERY request by design
//  (bug-report.routes.ts:106-116), so a visible control could not be made to
//  work by any user action — the only honest label would be a permanent
//  "reporting is unavailable" bolted to the sign-in screen. It is also the
//  posture apps/trust already shipped and documented, and one estate should
//  have one answer. What we do NOT do is degrade to a control that lies: once
//  the fence is configured and merely malfunctions (blocked script, timeout),
//  the gate says so out loud and the transport refuses to submit.
//
//  VITE_* is inlined at BUILD time, so the site key must also be a Docker
//  build-arg (admin-dashboard/Dockerfile) and a CI build input
//  (.github/workflows/deploy-ecs.yml). An image built without it behaves exactly
//  like the unconfigured case above, and no runtime env on the ECS task can
//  rescue it.
// ═══════════════════════════════════════════════════════════════════════════

import * as React from 'react';
import { ReportProvider } from '../ReportProvider';
import { ReportDialog } from '../ReportDialog';
import { ReportButton } from '../ReportButton';
import type { ReportEnvelope, ReporterConfig, SubmitResult } from '../types';
import { normalizeApiOrigin } from '@/lib/api-origin';
import { TurnstileGate, type TurnstileGateHandle } from './TurnstileGate';
import { useConsoleBuffer } from './useConsoleBuffer';

/** Shown when the challenge could not produce a token. Never a false success. */
const VERIFY_UNAVAILABLE =
  'the verification check could not be completed. Please try again, or email support@polestead.com.';

/**
 * The backend origin, read from the env directly rather than through
 * `@/lib/supabase`'s `apiUrl`.
 *
 * WHY NOT THE EXPORT. That module constructs a Supabase client at import time
 * and is mocked wholesale in this app's test setup, so importing ONE constant
 * from it drags a browser-only client into every suite that touches this file —
 * and the shared mock does not re-export `apiUrl`, which turns a missing test
 * fixture into an import-time crash of App.tsx. The value is one env read with
 * the same fallback (lib/supabase.ts:15); duplicating a `||` is cheaper than
 * that coupling.
 *
 * `normalizeApiOrigin` (lib/api-origin.ts, a pure function with no client in it)
 * tolerates a base that already carries the `/api/v1` prefix as well as a bare
 * origin, so the endpoint is right in either deployment shape: a report posted
 * to a path that does not exist is the failure this whole slice exists to
 * remove. customer-portal has its own lib/api-origin.ts with the same rule.
 */
const API_ORIGIN = normalizeApiOrigin((import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:4500');
const ANON_ENDPOINT = `${API_ORIGIN}/api/v1/reports/anonymous`;

/** Cloudflare Turnstile SITE key (public by design; the SECRET stays server-side). */
const SITE_KEY = ((import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '').trim();

/**
 * The floating affordance.
 *
 * <ReportButton> is reused verbatim so the glyph, the 38×38 geometry, the hover
 * treatment and the localised label are IDENTICAL to the one in AdminHeader —
 * the requirement is that a user sees the same control, not a second thing that
 * does the same job. What is added is only the standing: /login and
 * /reset-password have no header to sit in, so it gets a surface of its own to
 * be legible against a bare background.
 */
function FloatingLauncher() {
  return (
    <div
      // The reporter's own UI is never part of what it captures.
      className="ps-no-capture"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        // Below the dialog layer so an open dialog is never floated over.
        zIndex: 300,
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-md, 0 2px 8px rgba(0,0,0,0.12))',
        display: 'flex',
      }}
    >
      <ReportButton />
    </div>
  );
}

export function AnonymousReporter() {
  const gateRef = React.useRef<TurnstileGateHandle | null>(null);

  const configured = SITE_KEY.length > 0 && API_ORIGIN.length > 0;

  // Buffering starts on mount, not on dialog open — by the time an operator
  // decides to report "I cannot sign in", the failed POST /auth/v1/token that
  // explains it has already happened.
  const buffer = useConsoleBuffer({ disabled: !configured });

  const transport = React.useCallback(
    async (envelope: ReportEnvelope): Promise<SubmitResult> => {
      const token = await gateRef.current?.take();
      if (!token) {
        // No token ⇒ a guaranteed 401. Refusing here turns a blocked challenge
        // into a sentence the user can act on rather than a server fault.
        return { ok: false, error: VERIFY_UNAVAILABLE };
      }

      const collected = buffer.collect();
      const payload = {
        ...envelope,
        // Never present (captureDisabled ⇒ no events), but stripped explicitly
        // so a future provider change cannot start shipping a recording from an
        // unauthenticated origin by accident.
        captureGzipB64: undefined,
        capture: {
          ...envelope.capture,
          events: [],
          captured: false,
          masked: true,
          console: collected.console,
          network: collected.network,
          durationMs: collected.durationMs,
        },
        // The field name `requireTurnstile` actually reads
        // (bug-report.routes.ts:117).
        turnstileToken: token,
      };

      try {
        const res = await fetch(ANON_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          // There is no session cookie and none would be honoured.
          credentials: 'omit',
        });

        if (!res.ok) {
          // Backend errors are `{ error: { code, message } }`
          // (backend/src/middleware/error.ts). Keep the status when the body is
          // unreadable rather than inventing a reason.
          let detail = `HTTP ${res.status}`;
          try {
            const j = (await res.json()) as {
              error?: { message?: string } | string;
              message?: string;
            };
            const fromError =
              typeof j.error === 'string' ? j.error : j.error?.message ? j.error.message : undefined;
            detail = fromError || j.message || detail;
          } catch {
            /* keep the status */
          }
          return { ok: false, error: detail };
        }

        const j = (await res.json().catch(() => ({}))) as { ticket_ref?: string };
        return { ok: true, ticketRef: j.ticket_ref };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'network error' };
      }
    },
    [buffer],
  );

  const config = React.useMemo<ReporterConfig>(
    () => ({
      endpoint: ANON_ENDPOINT,
      surfaceKey: 'license.console',
      appVersion: import.meta.env.VITE_APP_VERSION as string | undefined,
      transport,
      // THE POINT OF THE WHOLE COMPONENT: nothing identifying.
      getIdentity: () => ({}),
      // No rrweb on a logged-out page: the server strips the recording on this
      // route. Console + network still travel, injected by the transport.
      captureDisabled: true,
      // Studio is a super-admin authoring mode; there is no super-admin here.
      studioAvailable: false,
    }),
    [transport],
  );

  // Unconfigured ⇒ no provider ⇒ no button, no dialog, no Cloudflare script,
  // and no globals patched. See the header for why this hides rather than
  // explains.
  if (!configured) return null;

  return (
    <ReportProvider config={config}>
      <FloatingLauncher />
      <ReportDialog />
      <TurnstileGate ref={gateRef} siteKey={SITE_KEY} />
    </ReportProvider>
  );
}

export default AnonymousReporter;
